import { useCallback, useState } from 'react';
import {
  HsmMessageLog,
  HsmCampaignStats
} from '../types';
import * as hsmService from '../services/hsm.service';
import { withAuthRetry } from '../utils/withAuthRetry';

export interface UseHsmStatsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageHsm: boolean;
}

export interface UseHsmStatsReturn {
  // State
  messageLogs: HsmMessageLog[];
  logsLoading: boolean;
  campaignStats: HsmCampaignStats | null;
  statsLoading: boolean;

  // Actions
  fetchMessageLogs: (limit?: number) => Promise<{ success: boolean; error?: string }>;
  fetchCampaignStats: () => Promise<{ success: boolean; error?: string }>;

  // Campaign execution
  runCampaign: () => Promise<{ success: boolean; error?: string; message?: string }>;
  runCampaignDay: (day: number) => Promise<{ success: boolean; error?: string; message?: string }>;
}

export function useHsmStats({
  accessToken,
  refreshToken,
  attemptRefresh,
  canManageHsm
}: UseHsmStatsProps): UseHsmStatsReturn {
  // State
  const [messageLogs, setMessageLogs] = useState<HsmMessageLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [campaignStats, setCampaignStats] = useState<HsmCampaignStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // API calls
  const fetchMessageLogs = useCallback(
    async (limit: number = 100): Promise<{ success: boolean; error?: string }> => {
      if (!accessToken || !canManageHsm) return { success: false, error: 'Nao autorizado' };

      setLogsLoading(true);

      try {
        const data = await withAuthRetry(
          () => hsmService.fetchMessageLogs(accessToken!, limit),
          refreshToken,
          attemptRefresh
        );
        setMessageLogs(Array.isArray(data) ? data : []);
        return { success: true };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao buscar logs'
        };
      } finally {
        setLogsLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh, canManageHsm]
  );

  const fetchCampaignStats = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageHsm) return { success: false, error: 'Nao autorizado' };

    setStatsLoading(true);

    try {
      const data = await withAuthRetry(
        () => hsmService.fetchCampaignStats(accessToken!),
        refreshToken,
        attemptRefresh
      );
      setCampaignStats(data);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao buscar estatisticas'
      };
    } finally {
      setStatsLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageHsm]);

  // Campaign execution
  const runCampaign = useCallback(async (): Promise<{ success: boolean; error?: string; message?: string }> => {
    if (!accessToken) return { success: false, error: 'Nao autenticado' };

    try {
      const result = await withAuthRetry(
        () => hsmService.runCampaign(accessToken!),
        refreshToken,
        attemptRefresh
      );
      await fetchCampaignStats();
      await fetchMessageLogs();
      return { success: true, message: result.message };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro ao executar campanha'
      };
    }
  }, [accessToken, refreshToken, attemptRefresh, fetchCampaignStats, fetchMessageLogs]);

  const runCampaignDay = useCallback(
    async (day: number): Promise<{ success: boolean; error?: string; message?: string }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      try {
        const result = await withAuthRetry(
          () => hsmService.runCampaignDay(day, accessToken!),
          refreshToken,
          attemptRefresh
        );
        await fetchCampaignStats();
        await fetchMessageLogs();
        return { success: true, message: result.message };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : `Erro ao executar dia ${day}`
        };
      }
    },
    [accessToken, refreshToken, attemptRefresh, fetchCampaignStats, fetchMessageLogs]
  );

  return {
    // State
    messageLogs,
    logsLoading,
    campaignStats,
    statsLoading,

    // Actions
    fetchMessageLogs,
    fetchCampaignStats,

    // Campaign execution
    runCampaign,
    runCampaignDay
  };
}
